/**
 * Form component for adding or editing status reason mapping parameters.
 *
 * @param {DrawerProps} props - Contains properties such as id, onCloseDrawer, and onRefreshList.
 * @returns {JSX.Element} - Returns the form layout with configuration parameter fields.
 *
 * This component fetches specific configuration parameter data if an id is provided.
 * It handles form submission to add or edit configuration information.
 * Displays success or error toast messages based on the operation result.
 */

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TConfigParam,
  TDropdownOptions,
  TResponseModel,
} from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditConfigInfo,
  GetConfigParamDataList,
  GetSpecificConfigParamData,
} from "../../../utils/api.constant";
import { CONFIG_GROUPS } from "../../../utils/constants";
import Utils from "../../../utils";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form component for adding or editing status reason mapping parameters.

 */
export default function StatusReasonForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [statusOptions, setStatusOptions] = useState<TDropdownOptions[]>([]);

  // Hook to handle on load operations
  useEffect(() => {
    fetchSpecificParam();
    fetchStatus();
  }, []);

  const defaultValues: TConfigParam = {
    id: 0,
    name: "",
    description: "",
    groupName: "",
    groupId: null,
    groupUniqueId: "",
    paramUniqueId: "",
  };

  const fetchStatus = async () => {
    try {
      const payload = {
        data: {
          groupUniqueId: CONFIG_GROUPS.TICKETSTATUS,
          pageSize: -1,
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(
        GetConfigParamDataList,
        payload
      );
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setStatusOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: TConfigParam) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
          groupUniqueId: CONFIG_GROUPS.STATUSREASON,
          refGroupUniqueId: CONFIG_GROUPS.TICKETSTATUS,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditConfigInfo, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        form.setFieldValue("configGroupId", null);
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificParam = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificConfigParamData, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const apiData = response.data;
          form.setFieldsValue({ ...apiData });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          <Row gutter={16} className="">
            <Col span={24}>
              <Form.Item
                name="name"
                label="Reason"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter Reason"),
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter the reason..."
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="refParamUniqueId"
                label="Status"
                rules={[{ required: true, message: "Please choose status" }]}
              >
                <Select
                  showSearch
                  placeholder="Select Status"
                  options={statusOptions}
                  onChange={(value, option: TDropdownOptions | any) => {
                    form.setFieldValue(
                      "refParamUniqueId",
                      option.data?.paramUniqueId
                    );
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <Input autoComplete="off" placeholder="Enter the description" />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              Save
            </Button>
            <Button
              type="primary"
              disabled={isLoading}
              onClick={props.onCloseDrawer}
              className="btnDimensions"
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
